import { Box, IconButton, Tooltip, tooltipClasses } from '@mui/material';
import { TypographyProps } from '@mui/material/Typography';
import { Check, Copy } from 'lucide-react';
import { useIntl } from 'react-intl';
import { useRecoilValue } from 'recoil';
import SensitiveInfoTypography from '~/components/SensitiveInfoTypography';
import { useCopyToClipboard } from '~/hooks/useCopyToClipboard';
import { showSensitiveInfoState } from '~/state/atoms/viewerAtoms';
import { cutUID } from '~/utils/string';
import { ValueText } from './LabeledValue';

type IdFieldProps = {
    id: string;
    label?: string;
    /**
     * Show the UID in full, wrapped over as many lines as it needs, so it
     * can be checked against the PACS or the TPS. Off by default: sidebar
     * rows and hover panels have no room for it.
     *
     * No effect while sensitive info is hidden: `cutUID` still applies.
     */
    full?: boolean;
    typographyProps?: TypographyProps;
};

export default function IdField({
    id,
    label,
    full = false,
    typographyProps,
}: IdFieldProps) {
    const intl = useIntl();
    const showSensitiveInfo = useRecoilValue(showSensitiveInfoState);
    const shownId = full && showSensitiveInfo ? id : cutUID(id);
    const { copy, status, reset } = useCopyToClipboard();
    const isCopied = status === 'copied';

    return (
        <Box
            display="flex"
            alignItems={full ? 'flex-start' : 'center'}
            gap={0.25}
            data-testid="id-field"
        >
            <ValueText
                component="span"
                sx={
                    full
                        ? {
                              // ValueText clips to one line; the full form
                              // wraps instead.
                              whiteSpace: 'normal',
                              wordBreak: 'break-all',
                              fontFamily: 'monospace',
                          }
                        : undefined
                }
                {...typographyProps}
            >
                <SensitiveInfoTypography>
                    {label ? `${label}: ${shownId}` : shownId}
                </SensitiveInfoTypography>
            </ValueText>
            <Tooltip
                PopperProps={{
                    sx: {
                        [`& .${tooltipClasses.tooltip}`]: {
                            maxWidth: 'none',
                        },
                    },
                }}
                title={showSensitiveInfo ? id : undefined}
                placement="right"
                disableHoverListener={!showSensitiveInfo}
                disableFocusListener={!showSensitiveInfo}
                disableTouchListener={!showSensitiveInfo}
            >
                {/* No height of its own: the button overhangs the line,
                    so a row of IDs is as high as a row of text. */}
                <Box
                    component="span"
                    display="flex"
                    alignItems="center"
                    height={0}
                >
                    <IconButton
                        size="small"
                        color={isCopied ? 'success' : 'primary'}
                        onClick={() => copy(id)}
                        onMouseLeave={reset}
                        disabled={!showSensitiveInfo}
                        aria-label={intl.formatMessage({ id: 'copyId' })}
                        data-testid="copy-id-button"
                    >
                        {isCopied ? <Check size={10} /> : <Copy size={10} />}
                    </IconButton>
                </Box>
            </Tooltip>
        </Box>
    );
}
